import { animate, style, transition, trigger } from '@angular/animations';
import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { forLifeOf, Interaction, LifecycleAware, Utilities } from '@lsq-frontend/common-ui';
import { LsqInputComponent } from '@lsq-frontend/common-ui';
import { debounceTime, distinctUntilKeyChanged, filter, tap } from 'rxjs/operators';
import { IGlobalSearch } from './global-search';
import { GlobalSearchField } from './global-search.constants';
import { GlobalSearchService } from './global-search.service';

@LifecycleAware()
@Component({
    selector: 'global-search',
    templateUrl: './global-search.component.html',
    styleUrls: ['./global-search.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: [
        trigger('slide', [
            // This must match the animation in common-ui _layout.scss.
            transition(':enter', [
                style({
                    transform: 'translateY(-100%)'
                }),
                animate(
                    '400ms cubic-bezier(0, 0.9, 0.2, 1)',
                    style({
                        transform: 'translateY(0)'
                    })
                )
            ]),
            transition(':leave', [
                animate(
                    '400ms cubic-bezier(0, 0.9, 0.2, 1)',
                    style({
                        transform: 'translateY(-100%)'
                    })
                )
            ])
        ])
    ]
})
export class GlobalSearchComponent implements OnInit, OnDestroy {
    @ViewChild(LsqInputComponent) public searchInput: LsqInputComponent;
    public maxSearchLength = Interaction.MaxGlobalSearchLength;
    public searchByInvoiceNumberKey = GlobalSearchField.InvoiceNumber;
    public searchByCheckNumberKey = GlobalSearchField.CheckNumber;
    public formGroup = new FormGroup({
        searchBy: new FormControl(this.searchByInvoiceNumberKey),
        search: new FormControl('')
    });

    constructor(public globalSearchService: GlobalSearchService) {}

    public ngOnInit(): void {
        this.globalSearchService.isExpanded$.pipe(forLifeOf(this)).subscribe((isExpanded) => {
            if (isExpanded) {
                this._setInputFocus(true);
            } else if (this.formGroup.get('search')) {
                this.formGroup.get('search').reset();
            }
        });

        // Do the search, debouncing.
        this.formGroup.valueChanges
            .pipe(
                forLifeOf(this),
                distinctUntilKeyChanged('search'),
                // Ignore the debounceTime when clearing the search.
                tap(({ searchBy, search }) => {
                    if (!search) {
                        this.doSearch({ searchBy, search });
                    }
                }),
                debounceTime(Interaction.SearchDebounceTimeMs),
                filter(({ search }) => !!search)
            )
            .subscribe(({ searchBy, search }) => {
                this.doSearch({ searchBy, search });
            });

        this.formGroup
            .get('searchBy')
            .valueChanges.pipe(forLifeOf(this))
            .subscribe(() => this._setInputFocus(true, true));
    }

    public ngOnDestroy(): void {
        this.globalSearchService.stopSearching();
    }

    public doSearch({ searchBy, search }: { searchBy: GlobalSearchField; search: string }): void {
        if (search && !!search.length) {
            const globalSearch: IGlobalSearch = {
                [searchBy]: search
            };
            this.globalSearchService.search(globalSearch);
        }
    }

    public handleSearchFormSubmit(): void {
        this.doSearch(this.formGroup.value);
    }

    private _setInputFocus(shouldFocus: boolean, select?: boolean): void {
        Utilities.executeAfterRender(() => {
            if (this.searchInput) {
                this.searchInput.setFocus(shouldFocus, select);
            }
        });
    }
}
